import {
  useFloating,
  FloatingPortal,
  autoUpdate,
  useDismiss,
  useInteractions,
} from '@floating-ui/react';
import { MinusCircleIcon, PlusCircleIcon } from '@heroicons/react/24/outline';
import * as HeroIcons from '@heroicons/react/24/outline';
import { Button } from '@ths/ui';
import { useState, useRef } from 'react';
import { useField } from 'react-final-form';

import { pluralize } from 'utilities/pluralize';

type IconName = keyof typeof HeroIcons;
interface EnhancedNumberPickerProps {
  name: string;
  labelText: string;
  min: number;
  max: number;
  placeholder?: string;
  icon?: IconName;
  children?: React.ReactNode;
  disabled?: boolean;
}

const EnhancedNumberPicker: React.FC<EnhancedNumberPickerProps> = ({
  name,
  min,
  max,
  labelText,
  icon,
  placeholder,
  children,
  disabled = false,
}) => {
  const { input } = useField(name);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { refs, floatingStyles, context } = useFloating({
    whileElementsMounted: autoUpdate,
    open: isOpen && !disabled,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
  });

  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  const NumberIcon = HeroIcons[icon || 'HomeIcon'];

  const handleToggle = () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }

    setIsOpen(true);
  };

  const handleIncrement = () => {
    const value = Number(input.value || 0);
    if (value < max) {
      input.onChange(value + 1);
    }
  };

  const handleDecrement = () => {
    const value = Number(input.value || 0);
    if (value > min) {
      input.onChange(value - 1);
    }
  };

  const referenceWidth =
    refs.reference.current?.getBoundingClientRect()?.width || undefined;
  const portalWidth = window.innerWidth < 1024 ? referenceWidth : undefined;

  return (
    <div className="relative" ref={containerRef}>
      <Button
        type="button"
        variant="neutral"
        disabled={disabled}
        fullWidth
        onClick={handleToggle}
        ref={refs.setReference}
        {...getReferenceProps()}
        className="h-auto justify-between px-3 py-2 text-sm font-normal"
      >
        <span className="flex items-center">
          <NumberIcon className="mr-2 size-5 text-text-tertiary" />
          <span>
            {input.value} {pluralize(input.value, labelText)}
          </span>
        </span>
      </Button>

      {isOpen && !disabled && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            className="absolute z-10 mt-2 rounded-md border border-gray-200 bg-white shadow-lg"
            style={{
              ...floatingStyles,
              width: portalWidth,
            }}
            {...getFloatingProps()}
          >
            <div className="flex items-center justify-center gap-4 p-4 lg:justify-between lg:gap-2">
              <button onClick={handleDecrement}>
                <MinusCircleIcon className="size-6 text-gray-400" />
              </button>
              <input
                type="number"
                value={input.value}
                placeholder={placeholder}
                onChange={input.onChange}
                className="w-16 rounded-md border border-gray-200 px-2 py-1 text-center text-lg [appearance:textfield] focus:border-primaryBlue focus:ring-1 focus:ring-primaryBlue [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                min={min}
                max={max}
              />
              <button onClick={handleIncrement}>
                <PlusCircleIcon className="size-6 text-gray-400" />
              </button>
            </div>
            {children}
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

export default EnhancedNumberPicker;
